import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch, useFormContext, DefaultValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Button } from '@/components/ui-kit/button';
import {
  SharedPasswordStrengthChecker,
  PasswordInput,
  Captcha,
  useCaptcha,
} from '@/components/core';
import { Input } from '@/components/ui-kit/input';



export type BasePasswordFormData = {
  password: string;
  confirmPassword: string;
  firstName?: string;
  lastName?: string;
};

interface BasePasswordFormProps<T extends BasePasswordFormData> {
  code: string;
  onSubmit: (
    password: string,
    code: string,
    captchaToken?: string,
    formData?: T
  ) => Promise<void>;
  validationSchema: z.ZodType<T>;
  defaultValues: DefaultValues<T>;
  isPending: boolean;
  isCaptchaValid?: boolean;
  onCaptchaValidation?: (isValid: boolean) => void;
  showNameFields?: boolean;
}

const PasswordStrengthWatcher = () => {
  const { control } = useFormContext();
  const password = useWatch({ control, name: 'password' }) || '';
  const confirmPassword = useWatch({ control, name: 'confirmPassword' }) || '';

  return (
    <SharedPasswordStrengthChecker
      password={password}
      confirmPassword={confirmPassword}
      onRequirementsMet={() => {
        // no-op because submit blocking relies on Zod schema now
      }}
    />
  );
};

export const BasePasswordForm = <T extends BasePasswordFormData>({
  code,
  onSubmit,
  validationSchema,
  defaultValues,
  isPending,
  onCaptchaValidation,
  showNameFields,
}: Readonly<BasePasswordFormProps<T>>) => {
  const navigate = useNavigate();
  const [showCaptcha, setShowCaptcha] = useState(false);
  const { t } = useTranslation();

  const googleSiteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY || '';
  const captchaEnabled = googleSiteKey !== '';
  const { execute, captcha } = useCaptcha({
    siteKey: googleSiteKey,
    type: 'v3',
  });



  const form = useForm<T>({
    defaultValues,
    resolver: zodResolver(validationSchema),
  });

  useEffect(() => {
    if (captchaEnabled) {
      setShowCaptcha(true);
    }
  }, [captchaEnabled]);

  const onSubmitHandler = async (values: T) => {
    let currentToken: string | undefined;
    if (captchaEnabled && captcha.type === 'v3') {
      currentToken = await execute();
    }

    if (captchaEnabled && !currentToken) {
      return;
    }

    try {
      await onSubmit(values.password, code, captchaEnabled ? currentToken : undefined, values);
      navigate('/success');
    } catch (_error) {
      // Handle error if needed
    }
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4 max-h-[75vh] overflow-y-auto pr-5"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'hsl(var(--neutral-300)) transparent',
        }}
        onSubmit={form.handleSubmit(onSubmitHandler)}
      >
        {showNameFields && (
          <>
            <FormField
              control={form.control}
              name={"firstName" as Path<T>}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-high-emphasis font-normal">
                    {t('FIRST_NAME')}
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t('ENTER_YOUR_FIRST_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={"lastName" as Path<T>}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-high-emphasis font-normal">{t('LAST_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('ENTER_YOUR_LAST_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <FormField
          control={form.control}
          name={"password" as Path<T>}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-high-emphasis font-normal">{t('PASSWORD')}</FormLabel>
              <FormControl>
                <PasswordInput autoComplete="new-password" placeholder={t('ENTER_YOUR_PASSWORD')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={"confirmPassword" as Path<T>}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-high-emphasis font-normal">
                {t('CONFIRM_PASSWORD')}
              </FormLabel>
              <FormControl>
                <PasswordInput autoComplete="new-password" placeholder={t('CONFIRM_YOUR_PASSWORD')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <PasswordStrengthWatcher />

        {captchaEnabled && showCaptcha && (
          <div className="my-4">
            <Captcha {...captcha} theme="light" size="normal" />
          </div>
        )}

        <div className="flex gap-10 mt-5">
          <Button
            className="flex-1 font-extrabold"
            size="lg"
            type="submit"
            loading={isPending}
            disabled={isPending}
          >
            {t('CONFIRM')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
